import { Card, Flex, Icon } from "@repo/ping-design";
import { useState, useEffect, useRef, useCallback } from "react";
import PlacesAutocomplete from "react-places-autocomplete";
import { LoadingSpinner } from "../misc";
import "./GeocoderSearch.scss";

const KEYBOARD_ENTER = "Enter";

export type GeocoderSearchResult = {
  formattedAddress: string;
  lat: number;
  lng: number;
};

type GeocoderSearchProps = {
  defaultAddress?: string;
  onSelectAutoCompleteAddress?: (
    address: string,
    latLng: google.maps.LatLngLiteral,
  ) => void;
  onChange?: (address: string) => void;
  locationBias?: { lat: number; lng: number };
  onGeocode: (address: string) => Promise<GeocoderSearchResult | null>;
  isLoading?: boolean;
  classNamePrefix?: string;
};

export const GeocoderSearch = ({
  defaultAddress = "",
  onSelectAutoCompleteAddress,
  onChange,
  locationBias,
  onGeocode,
  isLoading = false,
  classNamePrefix = "GeocoderSearch",
}: GeocoderSearchProps) => {
  const [address, setAddress] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(
    async (value: string) => {
      const result = await onGeocode(value);
      setAddress("");

      if (!result) {
        console.log("No location data found for the selected address.");
        return;
      }

      const latLng: google.maps.LatLngLiteral = {
        lat: result.lat,
        lng: result.lng,
      };
      onSelectAutoCompleteAddress?.(result.formattedAddress, latLng);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [onGeocode, onSelectAutoCompleteAddress],
  );

  useEffect(() => {
    setAddress(defaultAddress);
  }, [defaultAddress]);

  const searchOptions: {
    types?: string[];
    location?: google.maps.LatLng | google.maps.LatLngLiteral;
    radius?: number | string;
  } = {
    types: ["establishment", "geocode"],
  };

  // add location bias to auto-complete search results, search within 5000 meters of ping geocoding lat/lng
  if (locationBias?.lat && locationBias?.lng) {
    searchOptions.location = {
      lat: locationBias.lat,
      lng: locationBias.lng,
    };
    searchOptions.radius = 5000;
  }

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KEYBOARD_ENTER) {
      const result = await onGeocode(address);

      if (!result) {
        console.log("No location data found for the selected address.");
        return;
      }

      const latLng: google.maps.LatLngLiteral = {
        lat: result.lat,
        lng: result.lng,
      };
      onSelectAutoCompleteAddress?.(result.formattedAddress, latLng);
    }
  };

  const handleChange = (value: string) => {
    onChange?.(value);
    setAddress(value);
  };

  return (
    <div className={classNamePrefix}>
      <PlacesAutocomplete
        value={address}
        onChange={handleChange}
        onSelect={handleSelect}
        searchOptions={searchOptions}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps }) => {
          const inputProps = getInputProps({
            placeholder: "Search address",
            className: `${classNamePrefix}__Input`,
          });

          const handleComposedKeyDown = (
            event: React.KeyboardEvent<HTMLInputElement>,
          ) => {
            inputProps.onKeyDown(event);
            handleKeyDown(event);
          };

          return (
            <div>
              <Flex gap="5" align="center">
                <div
                  className={`${classNamePrefix}__InputContainer`}
                  ref={containerRef}
                >
                  <Icon
                    iconName="Search"
                    size={16}
                    className={`${classNamePrefix}__InputContainer__Icon`}
                  />
                  <input
                    ref={inputRef}
                    {...inputProps}
                    onKeyDown={handleComposedKeyDown}
                    disabled={isLoading}
                  />

                  {isLoading && <LoadingSpinner size="xs" />}
                </div>
              </Flex>
              {suggestions.length > 0 && (
                <Card className={`${classNamePrefix}__SuggestionsContainer`}>
                  {suggestions.map((suggestion) => (
                    <div
                      className={`${classNamePrefix}__Suggestion`}
                      {...getSuggestionItemProps(suggestion)}
                      key={suggestion.placeId}
                    >
                      {suggestion.description}
                    </div>
                  ))}
                </Card>
              )}
            </div>
          );
        }}
      </PlacesAutocomplete>
    </div>
  );
};

GeocoderSearch.displayName = "GeocoderSearch";
